/* =====================================================================
   ENORMITY – Campaign motion system (milestone 1: intro, hero, Gold Thread, manifest)
   - GSAP + ScrollTrigger lokálně v tématu (assets/en-gsap.min.js, en-scrolltrigger.min.js)
   - každá sekce ve vlastním gsap.context(); úklid při shopify:section:unload/load
   - respektuje prefers-reduced-motion i tlačítko pauzy (html.en-motion-paused)
   - žádné wheel listenery, žádný pin/scroll-hijack; ScrollTrigger jen čte scroll
   Motion hierarchy: L1 produkt/scéna · L2 titulky + Gold Thread · L3 mikrointerakce (odlesk na E)
   ===================================================================== */
(() => {
  'use strict';
  if (window.__enCampaign) return; window.__enCampaign = true;

  const root = document.documentElement;
  const reduceMQ = matchMedia('(prefers-reduced-motion: reduce)');
  const mobileMQ = matchMedia('(max-width: 760px)');
  const paused = () => reduceMQ.matches || root.classList.contains('en-motion-paused');
  const hasGsap = () => typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';
  const E_POS = { x: 0.632, y: 0.362 };   // poloha vyšitého E v záběru mikiny (4:5)

  let hero, manifest, video, glint, thread, chapter, beanie, tshirt, drop;
  let ctxHero = null, ctxManifest = null, ctxThread = null, ctxChapter = null, ctxBeanie = null, ctxTshirt = null, ctxDrop = null;
  // Body zájmu v HQ snímcích mikiny (normalizované 0–1).
  const HOODIE = { e: { x: .684, y: .351 }, teddy: { x: .30, y: .79 }, hood: { x: .36, y: .13 }, hem: { x: .5, y: .94 } };
  const lbH = () => parseFloat(getComputedStyle(root).getPropertyValue('--en-launchbar-h')) || 0;
  let introDone = true, videoArmed = false, videoStarted = false, revealUntil = 0;

  /* ---------------- 01 INTRO ---------------- */
  function setupIntro() {
    const intro = hero.querySelector('[data-enc-intro]');
    introDone = !root.classList.contains('en-intro-play');
    if (introDone || !intro) { root.classList.remove('en-intro-play'); introDone = true; return; }
    try { localStorage.setItem('en_intro_seen', '1'); } catch (e) {}
    intro.addEventListener('animationend', e => { if (e.target === intro) finishIntro(); });
    setTimeout(finishIntro, 1300); // pojistka, kdyby animationend nepřišel
  }
  function finishIntro() {
    if (introDone) return;
    introDone = true;
    root.classList.remove('en-intro-play');
    revealUntil = performance.now() + 350;
    document.dispatchEvent(new CustomEvent('enormity:intro-done')); // popup předprodeje se otevře až teď
    tryStartVideo();
  }

  /* ---------------- 02 HERO VIDEO ---------------- */
  function armVideo() {
    if (videoArmed || paused() || !video) return;
    videoArmed = true;
    video.src = mobileMQ.matches ? video.dataset.srcMobile : video.dataset.srcDesktop;
    video.preload = 'auto';
    video.addEventListener('canplay', tryStartVideo);
    video.addEventListener('ended', () => pulseGlint(), { once: true });
    video.load();
  }
  function tryStartVideo() {
    if (videoStarted || !videoArmed || paused() || !introDone) return;
    if (root.classList.contains('en-presale-gated')) return;   // počká na ROZUMÍM
    if (video.readyState < 2) return;                         // počká na canplay
    videoStarted = true;
    if (performance.now() < revealUntil) {
      // Uživatel právě vidí tmu (konec intra / zavření popupu) -> rozsvícení od začátku záběru.
      video.classList.add('is-instant', 'is-live');
      video.currentTime = 0;
    } else {
      // Poster už svítí -> naváže se v rozsvícené části záběru a prolne se.
      try { video.currentTime = 2.2; } catch (e) {}
      video.classList.add('is-live');
    }
    const p = video.play();
    if (p && p.catch) p.catch(() => { video.classList.remove('is-live'); });
  }
  function pulseGlint() {
    if (!glint || paused() || !hasGsap()) return;
    const tw = () => gsap.fromTo(glint, { opacity: 0, scale: .6 }, { opacity: .9, scale: 1, duration: .55, ease: 'power2.out', yoyo: true, repeat: 1 });
    ctxHero ? ctxHero.add(tw) : tw();   // v kontextu -> revert při teardownu
  }
  // Poloha E v rámečku média při object-fit: cover (video 4:5, rámeček 4:5 na desktopu / 1:1 na mobilu).
  function ePos(w, h) {
    const VAR = 0.8, box = w / h;
    if (box > VAR) { const vh = w / VAR; return { x: E_POS.x, y: (E_POS.y * vh - (vh - h) / 2) / h }; }
    const vw = h * VAR; return { x: (E_POS.x * vw - (vw - w) / 2) / w, y: E_POS.y };
  }
  function placeGlint() {
    const media = hero && hero.querySelector('[data-enc-media]');
    if (!glint || !media) return;
    const p = ePos(media.offsetWidth, media.offsetHeight);
    glint.style.setProperty('--gx', (p.x * 100) + '%');
    glint.style.setProperty('--gy', (p.y * 100) + '%');
  }

  /* ---------------- GOLD THREAD ---------------- */
  // Jedna SVG vrstva přes hero + manifest. Cesta: ze zlatého E na mikině dolů ze scény,
  // měkkým ohybem k rozsvícenému slovu manifestu (a pod ním krátké „podtržení světlem“).
  function ensureThreadSvg() {
    const main = document.getElementById('MainContent');
    if (!main) return null;
    let svg = main.querySelector(':scope > .enc-thread');
    if (!svg) {
      svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'enc-thread');
      svg.setAttribute('aria-hidden', 'true');
      svg.innerHTML = '<defs><linearGradient id="encThreadGrad" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#f3dca8" stop-opacity=".95"/><stop offset=".55" stop-color="#d5b77b" stop-opacity=".75"/><stop offset="1" stop-color="#f3dca8" stop-opacity=".95"/></linearGradient></defs>'
        + '<path class="enc-thread__glow"/><path class="enc-thread__core"/><path class="enc-thread__under"/><path class="enc-thread__bridge"/>';
      main.prepend(svg);
    }
    return svg;
  }
  function layoutThread() {
    if (!thread || !manifest) return null;
    const main = document.getElementById('MainContent');
    const litLine = manifest.querySelector('[data-enc-lit]') || manifest.querySelector('.enc-line');
    const media = hero.querySelector('[data-enc-media]');
    if (!main || !litLine || !media) return null;
    // Měříme netransformované prvky (řádek <p>, ne animovaný <span>), aby cesta neujížděla během revealu.
    const m = main.getBoundingClientRect(), h = hero.getBoundingClientRect();
    const mr = media.getBoundingClientRect();
    const r = { left: mr.left, top: mr.top, width: media.offsetWidth, height: media.offsetHeight };
    r.left = mr.left + (mr.width - r.width) / 2; r.top = mr.top + (mr.height - r.height) / 2;   // bez scale z L1
    const word = litLine.firstElementChild;
    const lp = litLine.getBoundingClientRect();
    const wl = lp.left + (lp.width - word.offsetWidth) / 2, wr = wl + word.offsetWidth;
    const lefts = [...manifest.querySelectorAll('.enc-line')].map(el => { const b = el.getBoundingClientRect(), w = el.firstElementChild.offsetWidth; return getComputedStyle(el).textAlign === 'left' || getComputedStyle(manifest).textAlign === 'left' ? b.left : b.left + (b.width - w) / 2; });
    const X = v => (v - m.left).toFixed(1), Y = v => (v - m.top).toFixed(1);
    const ep = ePos(r.width, r.height);
    const ex = r.left + r.width * ep.x, ey = r.top + r.height * ep.y + r.height * .035;
    const hb = h.bottom;
    const gx = Math.max(m.left + 10, Math.min(...lefts) - (mobileMQ.matches ? 14 : 56));
    const base = lp.bottom - lp.height * .08;                  // pod účaří zvýrazněného řádku
    // Jeden plynulý S-oblouk ze scény k levé hraně textu, pak nit přejde v „podtržení světlem“.
    let d;
    if (mobileMQ.matches) {
      // Mobil: text leží pod scénou -> nit uhne k pravému okraji, sjede mimo titul a teprve pod hero se stočí doleva.
      const rx = m.right - 12, h0 = Math.min(ey + 90, r.top + r.height - 20);
      // Přechod zprava doleva proběhne v mezeře nad prvním řádkem manifestu, pak nit sjede levým okrajem.
      const ft = manifest.querySelector('.enc-line').getBoundingClientRect().top, gap = ft - hb;
      d = `M${X(ex)},${Y(ey)} C${X(ex)},${Y(ey + 50)} ${X(rx)},${Y(ey + 30)} ${X(rx)},${Y(h0)} L${X(rx)},${Y(hb - 20)} `
        + `C${X(rx)},${Y(hb + gap * .7)} ${X(gx)},${Y(hb + gap * .2)} ${X(gx)},${Y(ft - 10)} `
        + `L${X(gx)},${Y(base - 22)} Q${X(gx)},${Y(base)} ${X(gx + 22)},${Y(base)} L${X(wr)},${Y(base)}`;
    } else {
      // Desktop: oblouk doleva proběhne v mezeře nad manifestem, nit pak sjede podél levé hrany textu.
      const ft = manifest.querySelector('.enc-line').getBoundingClientRect().top, gap = ft - hb;
      d = `M${X(ex)},${Y(ey)} L${X(ex)},${Y(hb - 30)} `
        + `C${X(ex)},${Y(hb + gap * .75)} ${X(gx)},${Y(hb + gap * .15)} ${X(gx)},${Y(ft - 6)} `
        + `L${X(gx)},${Y(base - 26)} Q${X(gx)},${Y(base)} ${X(gx + 26)},${Y(base)} L${X(wr)},${Y(base)}`;
    }
    thread.style.height = Math.ceil(base - m.top + 40) + 'px';
    let bridge = thread.querySelector('.enc-thread__bridge');
    if (!bridge) { bridge = document.createElementNS('http://www.w3.org/2000/svg', 'path'); bridge.setAttribute('class', 'enc-thread__bridge'); thread.appendChild(bridge); }
    let blen = 0;
    const frame = chapter && chapter.querySelector('[data-enc-frame]');
    if (frame) {
      // Most: z konce podtržení dolů do kapitoly, přesně nad polohu vyšitého E (vstup do scény).
      const c = chapter.getBoundingClientRect(), sc = chapter.querySelector('[data-enc-scene]').getBoundingClientRect();
      const fx = sc.left + frame.offsetLeft + frame.offsetWidth * HOODIE.e.x;
      const ct = c.top;
      const bd = `M${X(wr)},${Y(base)} C${X(wr + 60)},${Y(base)} ${X(wr + 60)},${Y(base + (ct - base) * .45)} ${X((wr + fx) / 2)},${Y(base + (ct - base) * .6)} `
        + `S${X(fx)},${Y(ct - (ct - base) * .15)} ${X(fx)},${Y(ct + 2)}`;
      bridge.setAttribute('d', bd);
      thread.style.height = Math.ceil(ct - m.top + 8) + 'px';
      blen = bridge.getTotalLength(); bridge.style.strokeDasharray = blen;
    } else bridge.setAttribute('d', '');
    const [glow, core, und] = thread.querySelectorAll('path');
    glow.setAttribute('d', d); core.setAttribute('d', d); und.setAttribute('d', '');
    const grad = thread.querySelector('#encThreadGrad');
    grad.setAttribute('x1', X(ex)); grad.setAttribute('y1', Y(ey)); grad.setAttribute('x2', X(wr)); grad.setAttribute('y2', Y(base));
    const len = core.getTotalLength();
    [glow, core].forEach(p => { p.style.strokeDasharray = len; });
    return { len, ulen: 0, glow, core, und, bridge, blen };
  }

  /* ---------------- INIT / TEARDOWN ---------------- */
  let staticRelayout = 0;
  addEventListener('resize', () => { if (ctxHero) return; clearTimeout(staticRelayout); staticRelayout = setTimeout(() => { layoutBeanie(); if (hero && layoutThread()) thread.querySelectorAll('path').forEach(p => { p.style.strokeDashoffset = 0; }); }, 200); }, { passive: true });
  function initStatic() {
    root.classList.remove('enc-motion-on');
    layoutBeanie();
    beanie && beanie.querySelector('[data-enc-bvideo]')?.pause();
    const dt = layoutDrop(); if (dt) dt.path.style.strokeDashoffset = 0;
    // Omezený pohyb / pauza: finální stavy, žádné timeliny (přepočet cesty při resize výše a po načtení písma).
    document.fonts && document.fonts.ready.then(() => { layoutBeanie(); if (!ctxHero && hero) { const t = layoutThread(); t && [t.glow, t.core, t.bridge].forEach(p => { p.style.strokeDashoffset = 0; }); } });
    const t = layoutThread();
    if (t) { [t.glow, t.core, t.bridge].forEach(p => { p.style.strokeDashoffset = 0; }); }
    manifest && manifest.querySelectorAll('[data-enc-lit]').forEach(el => el.classList.add('is-lit'));
    if (video) { video.pause(); video.classList.remove('is-live'); }
  }

  function initMotion() {
    const { gsap, ScrollTrigger } = window;
    gsap.registerPlugin(ScrollTrigger);
    const mobile = mobileMQ.matches;
    root.classList.add('enc-motion-on');

    // L1: scéna produktu při odchodu z hero (jen transform/opacity).
    ctxHero = gsap.context(() => {
      gsap.to('[data-enc-media]', {
        scale: mobile ? .94 : .9, yPercent: mobile ? 4 : 7, opacity: .35, ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true }
      });
    }, hero);

    // L2: manifest – desktop scrub po řádcích, mobil jednorázový reveal (jiná dramaturgie).
    if (manifest) ctxManifest = gsap.context(() => {
      const lines = gsap.utils.toArray('.enc-line > span');
      if (mobile) {
        gsap.from(lines, { yPercent: 110, duration: .9, ease: 'power3.out', stagger: .12,
          scrollTrigger: { trigger: manifest, start: 'top 72%', toggleActions: 'play none none reverse' } });
      } else {
        lines.forEach(el => gsap.from(el, { yPercent: 110, ease: 'none',
          scrollTrigger: { trigger: el.parentElement, start: 'top 88%', end: 'top 58%', scrub: .5 } }));
      }
    }, manifest);

    // L2: Gold Thread – kreslí se scrollem z E na mikině k rozsvícenému slovu.
    if (manifest) ctxThread = gsap.context(() => {
      let t = layoutThread();
      if (!t) return;
      const litEl = manifest.querySelector('[data-enc-lit]');
      gsap.set([t.glow, t.core], { strokeDashoffset: t.len });
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: hero, start: 'top top', endTrigger: litEl || manifest, end: mobile ? 'top 70%' : 'top 62%', scrub: .6,
          invalidateOnRefresh: true,
          onRefreshInit: () => { placeGlint(); t = layoutThread() || t; },
          onUpdate: self => litEl && litEl.classList.toggle('is-lit', self.progress > .97)
        }
      });
      tl.fromTo(glint, { opacity: 0, scale: .7 }, { opacity: .85, scale: 1, duration: .08, ease: 'none' }, 0)
        .to([t.glow, t.core], { strokeDashoffset: 0, duration: .9, ease: 'none' }, 0.02)
        .to(glint, { opacity: .25, duration: .2, ease: 'none' }, .3);
      // Most: z rozsvíceného slova dolů až k okraji kapitoly (L2), pokračuje ve scéně.
      if (t.blen) {
        gsap.set(t.bridge, { strokeDashoffset: t.blen });
        gsap.to(t.bridge, { strokeDashoffset: 0, ease: 'none', scrollTrigger: {
          trigger: litEl || manifest, start: mobile ? 'top 70%' : 'top 62%',
          endTrigger: chapter, end: () => `top ${lbH() + 1}px`, scrub: .6, invalidateOnRefresh: true } });
      }
    }, thread);

    if (chapter) initChapter(mobile);
    if (beanie) initBeanie(mobile);
    if (tshirt) initTshirt(mobile);
    if (drop) initDrop();

    // Písmo mění metriky -> přepočet cesty.
    document.fonts && document.fonts.ready.then(() => ScrollTrigger.refresh());
  }

  /* ---------------- 04 KAPITOLA 01: HOODIE (pinned scéna) ---------------- */
  // Kamera: frame s transform-origin 0 0; bod P (0–1) se dostane do středu rámu: x = W/2 - P.x*W*s.
  function cam(frame, p, s) {
    return { scale: s, x: () => frame.offsetWidth / 2 - p.x * frame.offsetWidth * s, y: () => frame.offsetHeight / 2 - p.y * frame.offsetHeight * s };
  }
  function initChapter(mobile) {
    const { gsap } = window;
    const scene = chapter.querySelector('[data-enc-scene]');
    const frame = chapter.querySelector('[data-enc-frame]');
    const shots = { front: frame.querySelector('[data-shot="front"]'), tq: frame.querySelector('[data-shot="tq"]'), back: frame.querySelector('[data-shot="back"]') };
    const dip = chapter.querySelector('[data-enc-dip]'), copy = chapter.querySelector('[data-enc-copy]'), cta = chapter.querySelector('[data-enc-cta]');
    const labels = [...chapter.querySelectorAll('[data-enc-label]')];
    const svg = chapter.querySelector('[data-enc-scene-thread]');
    const [pin, pout] = svg.querySelectorAll('path');
    root.classList.add('enc-motion-on');

    // Nit ve scéně: shora (kam dorazil most) do středu rámu = kam kamera přiveze E. Na konci: od lemu dolů ze scény.
    const layoutSceneThread = () => {
      const W = scene.clientWidth, H = scene.clientHeight;
      const fl = frame.offsetLeft, ft = frame.offsetTop, fw = frame.offsetWidth, fh = frame.offsetHeight;
      svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
      const sx = fl + fw * HOODIE.e.x, cx = fl + fw / 2, cy = ft + fh / 2;
      pin.setAttribute('d', `M${sx},0 C${sx},${H * .22} ${cx},${cy - H * .3} ${cx},${cy - fh * .06}`);
      const hy = ft + fh * HOODIE.hem.y;
      pout.setAttribute('d', `M${cx},${hy} C${cx},${hy + (H - hy) * .5} ${cx - 30},${H - 20} ${cx - 30},${H + 4}`);
      [pin, pout].forEach(p => { const l = p.getTotalLength(); p.style.strokeDasharray = l; p.style.strokeDashoffset = l; });
    };

    ctxChapter = gsap.context(() => {
      layoutSceneThread();
      gsap.set(frame, { x: 0, y: 0, scale: 1 });
      const setFocus = p => () => { frame.style.setProperty('--fx', p.x * 100 + '%'); frame.style.setProperty('--fy', p.y * 100 + '%'); };
      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          // Sticky scéna (CSS) – ScrollTrigger jen čte průběh; délka = výška sekce minus scéna.
          trigger: chapter, start: () => `top ${lbH()}px`, end: () => '+=' + Math.max(1, chapter.offsetHeight - scene.offsetHeight),
          scrub: .8, invalidateOnRefresh: true, onRefreshInit: layoutSceneThread
        }
      });
      // 0 – vstup: kontrové světlo, titul
      tl.fromTo(frame, { scale: .94, x: () => frame.offsetWidth * .03, y: () => frame.offsetHeight * .03 }, { scale: 1, x: 0, y: 0, duration: 1 }, 0)
        .to(frame, { '--ra': .9, duration: 1 }, 0)
        .to(pin, { strokeDashoffset: 0, duration: 2.6 }, .2)
        // 1 – E: kamera najede, světlo přejede po výšivce, nit se E dotkne
        .to(frame, { ...cam(frame, HOODIE.e, mobile ? 2.1 : 2.4), duration: 2 }, 1)
        .to(copy, { opacity: 0, y: -16, duration: .8 }, 1)
        .to(frame, { '--ra': .3, duration: 1 }, 1.2)
        .add(setFocus(HOODIE.e), 1.9)
        .fromTo(frame, { '--fa': 0 }, { '--fa': .85, duration: .6 }, 2.3)
        .fromTo(frame, { '--sx': '130%', '--sa': .75 }, { '--sx': '-30%', duration: 1.4 }, 2)
        .fromTo(labels[0] || {}, { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: .6 }, 2.2)
        .to(pin, { opacity: 0, duration: .6 }, 3.1).to(frame, { '--fa': 0, duration: .6 }, 3.1)                     // nit na chvíli zmizí
        // 2 – Teddy: přesun kamery
        .to(frame, { ...cam(frame, HOODIE.teddy, mobile ? 1.9 : 2.1), duration: 1.6 }, 3.6)
        .to(labels[0] || {}, { opacity: 0, y: -10, duration: .5 }, 3.7)
        .add(setFocus(HOODIE.teddy), 4.4)
        .fromTo(labels[1] || {}, { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: .6 }, 4.4)
        .to(frame, { '--fa': .75, duration: .5 }, 4.7);
      let t = 5.2;
      {
        // 3 – kapuce: světelný střih (dip) na 3/4 záběr, detail látky
        tl.to(dip, { opacity: .96, duration: .4 }, t)
          .to(labels[1] || {}, { opacity: 0, duration: .35 }, t).to(frame, { '--fa': 0, duration: .35 }, t)
          .set(shots.front, { opacity: 0 }, t + .45).set(shots.tq, { opacity: 1 }, t + .45)
          .set(frame, cam(frame, HOODIE.hood, 2), t + .45)
          .to(dip, { opacity: 0, duration: .45 }, t + .5)
          .fromTo(frame, { '--sx': '130%', '--sa': .75 }, { '--sx': '-30%', duration: 1.4 }, t + .8)
          .fromTo(labels[2] || {}, { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: .6 }, t + .9)
          .to(frame, { ...cam(frame, { x: HOODIE.hood.x + .08, y: HOODIE.hood.y + .06 }, 1.75), duration: 1.2 }, t + .8);
        t += 2.2;
      }
      // 4 – záda: dip, celek se sloganem, titul + CTA, nit odchází dál
      const last = shots.tq;
      tl.to(dip, { opacity: .96, duration: .4 }, t)
        .to(labels, { opacity: 0, duration: .35 }, t).to(frame, { '--fa': 0, '--sa': 0, duration: .35 }, t)
        .set(last, { opacity: 0 }, t + .45).set(shots.back, { opacity: 1 }, t + .45)
        .set(frame, { x: 0, y: 0, scale: 1 }, t + .45)
        .to(dip, { opacity: 0, duration: .55 }, t + .5)
        .to(frame, { '--ra': .9, duration: .8 }, t + .6)
        .to(copy, { opacity: 1, y: 0, duration: .7 }, t + .9)
        .to(cta, { opacity: 1, duration: .5 }, t + 1.2).set(cta, { pointerEvents: 'auto' }, t + 1.3)
        .to(pout, { strokeDashoffset: 0, duration: 1.2 }, t + 1.2);
      // CTA je průhledné, ale fokusovatelné: při fokusu klávesnicí scéna doskočí na konec, aby bylo vidět.
      const st = tl.scrollTrigger;
      const onFocus = () => { if (st && st.progress < .97) { scrollTo({ top: st.end, behavior: 'instant' }); gsap.set(cta, { opacity: 1, pointerEvents: 'auto' }); } };
      cta.addEventListener('focusin', onFocus);
      return () => cta.removeEventListener('focusin', onFocus);
    }, chapter);
  }

  /* ---------------- 05 KAPITOLA 02: BEANIE ---------------- */
  // Koncová (= statická) kompozice: kulich sedí na účaří slova „Tečka“ jako tečka.
  const BEANIE = { objL: .1, objB: .82, cuff: .555, macro: { x: .5, y: .47 },
    // hrana lemu v ostrém makru (en-beanie-macro.webp, čtverec): přímka přes 2 body
    mEdge: [{ x: .12, y: .567 }, { x: .95, y: .824 }] };
  function layoutBeanie() {
    if (!beanie) return;
    const scene = beanie.querySelector('[data-enc-bscene]'), frame = beanie.querySelector('[data-enc-bframe]');
    const wordLine = beanie.querySelector('[data-enc-bword]').parentElement, word = wordLine.firstElementChild;
    const after = beanie.querySelector('[data-enc-bafter]').parentElement;
    const S = scene.getBoundingClientRect(), L = wordLine.getBoundingClientRect(), A = after.getBoundingClientRect();
    const W = S.width, H = S.height, g = parseFloat(getComputedStyle(beanie.querySelector('[data-enc-btype]')).left) || 24;
    const fs = parseFloat(getComputedStyle(wordLine.closest('h2')).fontSize);
    const wordRight = L.left - S.left + word.offsetWidth, base = L.bottom - S.top - fs * .14;
    let fh, left, top;
    if (!mobileMQ.matches) {
      fh = Math.max(140, Math.min(H * .62, ((W - g) - wordRight) / .9 / .8, (base - H * .08) / BEANIE.objB));
      const fw = fh * .8;
      left = wordRight + fs * .03 - fw * BEANIE.objL; top = base - fh * BEANIE.objB;
    } else {
      const lab = beanie.querySelector('.enc-blabels'), labH = lab ? lab.offsetHeight : 60;
      const avail = H - (A.bottom - S.top) - labH - 120;            // místo pro štítky + CTA
      fh = Math.max(120, Math.min(H * .5, (W - 2 * g) / .8, avail / .9));
      const fw = fh * .8;
      left = W - g - fw * .92; top = A.bottom - S.top + 4 - fh * .12;
    }
    frame.style.setProperty('--bf-left', Math.round(left) + 'px');
    frame.style.setProperty('--bf-top', Math.round(top) + 'px');
    frame.style.setProperty('--bf-h', Math.round(fh) + 'px');
  }

  let bVideoArmed = false, bVideoIO = null;
  function armBeanieVideo() {
    if (bVideoArmed || bVideoIO || paused() || !beanie) return;
    const v = beanie.querySelector('[data-enc-bvideo]');
    bVideoIO = new IntersectionObserver(es => es.forEach(e => {
      if (!e.isIntersecting || paused()) return;
      bVideoIO.disconnect(); bVideoIO = null; bVideoArmed = true;
      v.src = mobileMQ.matches ? v.dataset.srcMobile : v.dataset.srcDesktop;
      v.preload = 'auto';
      const ready = () => v.classList.add('is-ready');
      v.addEventListener('loadeddata', ready, { once: true });
      v.addEventListener('canplay', ready, { once: true });
      v.load();
      // iOS Safari u nepřehraného videa často nenačte data – krátké tiché „naplnění“.
      const p = v.play(); if (p && p.then) p.then(() => v.pause()).catch(() => {});
    }), { rootMargin: '100% 0px 100% 0px' });   // až je kapitola do 1 viewportu
    bVideoIO.observe(beanie);
  }
  // Seek-when-ready: nový seek až po dokončení předchozího (plynulé v Chromiu i WebKitu, viz tools/media/scrub-test.html).
  const scrubbers = new WeakMap();
  function scrubber(v) {
    if (scrubbers.has(v)) return scrubbers.get(v);
    let target = 0, busy = false;
    const go = () => { if (busy || v.readyState < 1) return; if (Math.abs(v.currentTime - target) < .02) return; busy = true; v.currentTime = target; };
    v.addEventListener('seeked', () => { busy = false; go(); });
    v.addEventListener('loadedmetadata', go);
    const set = t => { target = Math.max(0, Math.min((v.duration || 8.8) - .05, t)); go(); };
    scrubbers.set(v, set);
    return set;
  }

  function initBeanie(mobile) {
    const { gsap } = window;
    const scene = beanie.querySelector('[data-enc-bscene]'), frame = beanie.querySelector('[data-enc-bframe]');
    const v = frame.querySelector('[data-enc-bvideo]'), weft = frame.querySelector('[data-enc-weft]');
    const mlayer = beanie.querySelector('[data-enc-bmacro]'), blur = beanie.querySelector('[data-enc-bblur]'), weft0 = beanie.querySelector('[data-enc-weft0]');
    const word = beanie.querySelector('[data-enc-bword]'), after = beanie.querySelector('[data-enc-bafter]'), kicker = beanie.querySelector('[data-enc-bkicker]');
    const labels = [...beanie.querySelectorAll('[data-enc-blabel]')], cta = beanie.querySelector('[data-enc-bcta]');
    const [pin, pout] = beanie.querySelectorAll('[data-enc-bthread] path'), svg = beanie.querySelector('[data-enc-bthread]');
    const seek = scrubber(v);
    armBeanieVideo();

    // Makro stav kamery: rám pokryje scénu, zaměřený na žebrování nad lemem.
    const macro = () => {
      const fw = frame.offsetWidth, fh = frame.offsetHeight, W = scene.clientWidth, H = scene.clientHeight;
      const s = Math.max(W / fw, H / fh) * (mobile ? 1.5 : 1.35);
      return { s, x: W / 2 - frame.offsetLeft - BEANIE.macro.x * fw * s, y: H / 2 - frame.offsetTop - BEANIE.macro.y * fh * s };
    };
    const layoutThreads = () => {
      layoutBeanie();
      const W = scene.clientWidth, H = scene.clientHeight, m = macro(), fw = frame.offsetWidth, fh = frame.offsetHeight;
      svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
      // vstup: z horní hrany (kde odešla nit z mikiny) na šikmou hranu lemu v ostrém makru (object-fit: cover čtverce)
      const D = Math.max(W, H), ox = (W - D) / 2, oy = (H - D) / 2;
      const [e1, e2] = BEANIE.mEdge.map(p => ({ x: ox + p.x * D, y: oy + p.y * D }));
      const cx = W / 2, cy = e1.y + (e2.y - e1.y) * ((cx - e1.x) / (e2.x - e1.x));
      const len = Math.hypot(e2.x - e1.x, e2.y - e1.y), ang = Math.atan2(e2.y - e1.y, e2.x - e1.x) * 180 / Math.PI;
      Object.assign(weft0.style, { width: len + 'px', left: ((e1.x + e2.x) / 2 - len / 2) + 'px', top: ((e1.y + e2.y) / 2) + 'px' });
      weft0.style.setProperty('--ang', ang + 'deg');
      if (window.gsap) gsap.set(weft0, { rotation: ang });
      const sx = W / 2 - 30;
      pin.setAttribute('d', `M${sx},0 C${sx},${cy * .45} ${cx},${cy * .55} ${cx},${cy}`);
      // výstup: z pravého konce nitě (koncový stav) dolů ze scény
      const ex = frame.offsetLeft + fw * .91, ey = frame.offsetTop + fh * BEANIE.cuff;
      pout.setAttribute('d', `M${ex},${ey} C${ex + 60},${ey} ${ex + 60},${ey + (H - ey) * .6} ${W / 2 - 30},${H + 4}`);
      [pin, pout].forEach(p => { const l = p.getTotalLength(); p.style.strokeDasharray = l; p.style.strokeDashoffset = l; });
    };

    ctxBeanie = gsap.context(() => {
      layoutThreads();
      const setInv = () => { const sc = gsap.getProperty(frame, 'scale') || 1; weft.style.setProperty('--inv', (1 / sc).toFixed(4)); };
      const vt = { t: 0 };
      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: beanie, start: () => `top ${lbH()}px`, end: () => '+=' + Math.max(1, beanie.offsetHeight - scene.offsetHeight),
          scrub: .8, invalidateOnRefresh: true, onRefreshInit: layoutThreads
        },
        onUpdate: setInv
      });
      const M = macro();
      gsap.set(frame, { x: M.x, y: M.y, scale: M.s });
      gsap.set(weft, { '--wx': 0 }); gsap.set([word, after], { yPercent: 110 }); gsap.set(kicker, { opacity: 0 });
      // 0 – makro, zaostření (rack focus), nit dopadá shora na hranu lemu
      gsap.set(weft0, { scaleX: 0, opacity: 1 });
      tl.fromTo(blur, { opacity: 1 }, { opacity: 0, duration: 1.4 }, 0)
        .to(pin, { strokeDashoffset: 0, duration: .9 }, 0)
        .fromTo(labels[0] || {}, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: .5 }, .6)
        // 1 – nit se stane útkem žebrování
        .fromTo(weft0, { scaleX: 0 }, { scaleX: 1, duration: 1, ease: 'power2.out', immediateRender: false }, .95)
        .fromTo(weft, { '--wx': 0 }, { '--wx': 1, duration: .01, immediateRender: false }, 1.9)
        // prolnutí: ostré makro -> záběr z videa (kamera už odjíždí)
        .to([mlayer, weft0], { opacity: 0, duration: .8 }, 2.05)
        .to(pin, { opacity: 0, duration: .5 }, 1.75)
        // 2 – kamera odjíždí, přechod na video, scroll řídí světlo
        .fromTo(frame, { x: () => macro().x, y: () => macro().y, scale: () => macro().s }, { x: 0, y: 0, scale: 1, duration: 3.8, ease: 'power2.inOut', immediateRender: false }, 2)
        .to(v, { opacity: 1, duration: .4 }, 2.5)
        .to(vt, { t: 8.8 * .55, duration: 3.5, onUpdate: () => seek(vt.t) }, 2.5)
        .to(labels[0] || {}, { opacity: 0, duration: .4 }, 2.6)
        .fromTo(labels[1] || {}, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: .5 }, 3.2)
        .to(labels[1] || {}, { opacity: 0, duration: .4 }, 5)
        // 3 – „Tečka“: slovo se odhalí, kulich je tečka; světlo rozsvítí zlaté E
        .fromTo(kicker, { opacity: 0 }, { opacity: 1, duration: .5, immediateRender: false }, 5.4)
        .fromTo(word, { yPercent: 110 }, { yPercent: 0, duration: 1.2, ease: 'power3.out', immediateRender: false }, 5.5)
        .to(vt, { t: 8.8 * .85, duration: 1.6, onUpdate: () => seek(vt.t) }, 6)
        .fromTo(labels[2] || {}, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: .5 }, 6.2)
        // 4 – druhý řádek, CTA, nit odchází ke kapitole 03
        .fromTo(after, { yPercent: 110 }, { yPercent: 0, duration: .9, ease: 'power3.out', immediateRender: false }, 7.2)
        .to(vt, { t: 8.7, duration: 1.2, onUpdate: () => seek(vt.t) }, 7.6)
        .to(cta, { opacity: 1, duration: .5 }, 7.8).set(cta, { pointerEvents: 'auto' }, 7.9)
        .to(pout, { strokeDashoffset: 0, duration: 1.4 }, 8.4);
      setInv();
      const st = tl.scrollTrigger;
      const onFocus = () => { if (st && st.progress < .97) { scrollTo({ top: st.end, behavior: 'instant' }); gsap.set(cta, { opacity: 1, pointerEvents: 'auto' }); } };
      cta.addEventListener('focusin', onFocus);
      return () => cta.removeEventListener('focusin', onFocus);
    }, beanie);
  }

  /* ---------------- 06 KAPITOLA 03: T-SHIRT ---------------- */
  const TSHIRT = { hem: .9 };
  function initTshirt(mobile) {
    const { gsap } = window;
    const scene = tshirt.querySelector('[data-enc-tscene]'), frame = tshirt.querySelector('[data-enc-tframe]');
    const wipe = frame.querySelector('[data-enc-wipe]');
    const ins = [...tshirt.querySelectorAll('[data-enc-insert]')];
    const slogan = [...tshirt.querySelectorAll('[data-enc-slogan] .enc-line > span')], sloganBox = tshirt.querySelector('[data-enc-slogan]');
    const kicker = tshirt.querySelector('[data-enc-tcopy] .enc-kicker'), t1 = tshirt.querySelector('[data-enc-tt1]'), t2 = tshirt.querySelector('[data-enc-tt2]');
    const state = tshirt.querySelector('[data-enc-tstate]'), cta = tshirt.querySelector('[data-enc-tcta]');
    const svg = tshirt.querySelector('[data-enc-tthread]'), [pin, pout] = svg.querySelectorAll('path');

    const layoutThreads = () => {
      const W = scene.clientWidth, H = scene.clientHeight;
      const fl = frame.offsetLeft, ft = frame.offsetTop, fw = frame.offsetWidth, fh = frame.offsetHeight;
      svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
      // vstup: z místa, kde odešla nit z kulichu, k levému hornímu okraji trička = start masky
      const sx = W / 2 - 30, ex = fl + fw * .02, ey = ft + fh * .12;
      pin.setAttribute('d', `M${sx},0 C${sx},${ey * .6} ${ex},${ey * .3} ${ex},${ey}`);
      const hx = fl + fw / 2, hy = ft + fh * TSHIRT.hem;
      pout.setAttribute('d', `M${hx},${hy} C${hx},${hy + (H - hy) * .55} ${hx - 30},${H - 16} ${hx - 30},${H + 4}`);
      [pin, pout].forEach(p => { const l = p.getTotalLength(); p.style.strokeDasharray = l; p.style.strokeDashoffset = l; });
    };

    ctxTshirt = gsap.context(() => {
      layoutThreads();
      const shift = () => mobile ? 0 : -Math.min(scene.clientWidth * .2, scene.clientWidth / 2 - frame.offsetWidth * .43 - 40);
      gsap.set(slogan, { yPercent: 140 }); gsap.set([t1, t2], { yPercent: 110 });
      const caps = ins.map(f => f.querySelector('figcaption'));
      gsap.set(caps, { opacity: 0 });
      gsap.set([state, kicker], { opacity: 0 });
      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: tshirt, start: () => `top ${lbH()}px`, end: () => '+=' + Math.max(1, tshirt.offsetHeight - scene.offsetHeight),
          scrub: .8, invalidateOnRefresh: true, onRefreshInit: layoutThreads
        }
      });
      // 0 – vstup: předek, nit přichází k okraji trička
      tl.fromTo(frame, { scale: .92 }, { scale: 1, duration: 1.2 }, 0)
        .fromTo(kicker, { opacity: 0 }, { opacity: 1, duration: .6, immediateRender: false }, mobile ? 8.0 : .3)
        .to(pin, { strokeDashoffset: 0, duration: 1.4 }, 0)
        // 1 – dvojstrana: tričko ustoupí, odkryje se makro E, pak Teddy + štítek
        .to(frame, { x: shift, scale: mobile ? .94 : .86, duration: 1, ease: 'power2.inOut' }, 1.2)
        .fromTo(ins[0], { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: .9, ease: 'power2.out', immediateRender: false }, 1.6)
        .fromTo(caps[0] || {}, { opacity: 0 }, { opacity: 1, duration: .4, immediateRender: false }, 2.2)
        .to(caps[0] || {}, { opacity: 0, duration: .3 }, 3.1)
        .fromTo(ins[1], { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: .9, ease: 'power2.out', immediateRender: false }, 3.2)
        .fromTo(caps[1] || {}, { opacity: 0 }, { opacity: 1, duration: .4, immediateRender: false }, 3.9)
        .to(ins[0], { clipPath: 'inset(0% 0% 100% 0%)', duration: .9, ease: 'power2.in' }, 3.2)
        .to(caps[1] || {}, { opacity: 0, duration: .3 }, 4.9)
        .to(ins[1], { clipPath: 'inset(0% 0% 100% 0%)', duration: .7, ease: 'power2.in' }, 5.0)
        .to(frame, { x: 0, scale: 1, duration: .9, ease: 'power2.inOut' }, 5.6)
        // 2 – Gold Thread jako maska: nit se stane hranou přechodu předek → záda
        .to(pin, { opacity: 0, duration: .4 }, 6.3)
        .fromTo(wipe, { opacity: 0 }, { opacity: 1, duration: .3, immediateRender: false }, 6.2)
        .fromTo(frame, { '--tw': '0%' }, { '--tw': '100%', duration: 1.6, ease: 'power1.inOut', immediateRender: false }, 6.4)
        .to(wipe, { opacity: 0, duration: .4 }, 7.7)
        // 3 – slogan ze zad vyroste za produktem
        .fromTo(slogan[0] || {}, { yPercent: 140 }, { yPercent: 0, duration: .8, ease: 'power3.out', immediateRender: false }, 6.4)
        .fromTo(slogan[1] || {}, { yPercent: 140 }, { yPercent: 0, duration: .8, ease: 'power3.out', immediateRender: false }, 6.9)
        .fromTo(slogan[2] || {}, { yPercent: 140 }, { yPercent: 0, duration: .8, ease: 'power3.out', immediateRender: false }, 7.4)
        .fromTo(sloganBox, { color: mobile ? 'rgba(242,238,229,.2)' : 'rgba(242,238,229,.11)' }, { color: mobile ? 'rgba(243,220,168,.42)' : 'rgba(243,220,168,.22)', duration: .8, immediateRender: false }, 7.1)
        .to(sloganBox, mobile ? { opacity: 0, duration: .5 } : { color: 'rgba(242,238,229,.06)', duration: .6 }, mobile ? 8.0 : 7.9)
        // 4 – titul, věta, CTA; nit odchází z lemu dál
        .fromTo(t1, { yPercent: 110 }, { yPercent: 0, duration: .8, ease: 'power3.out', immediateRender: false }, 8.1)
        .fromTo(t2, { yPercent: 110 }, { yPercent: 0, duration: .8, ease: 'power3.out', immediateRender: false }, 8.4)
        .fromTo(state, { opacity: 0 }, { opacity: 1, duration: .6, immediateRender: false }, 8.8)
        .to(cta, { opacity: 1, duration: .5 }, 9.0).set(cta, { pointerEvents: 'auto' }, 9.1)
        .to(pout, { strokeDashoffset: 0, duration: 1.3 }, 9.4);
      const st = tl.scrollTrigger;
      const onFocus = () => { if (st && st.progress < .97) { scrollTo({ top: st.end, behavior: 'instant' }); gsap.set(cta, { opacity: 1, pointerEvents: 'auto' }); } };
      cta.addEventListener('focusin', onFocus);
      return () => cta.removeEventListener('focusin', onFocus);
    }, tshirt);
  }

  /* ---------------- 09 DROP INFO: nit dobíhá a podtrhne „Drop 001“ ---------------- */
  function layoutDrop() {
    if (!drop) return null;
    const svg = drop.querySelector('[data-enc-drop-thread]'), path = svg.querySelector('path'), k = drop.querySelector('[data-enc-drop-kicker]');
    const D = drop.getBoundingClientRect(), K = k.getBoundingClientRect();
    const W = D.width, x0 = W / 2 - 30, kx = K.left - D.left, ky = K.bottom - D.top + 6, kr = kx + Math.max(K.width, 120);
    svg.setAttribute('viewBox', `0 0 ${W} ${D.height}`);
    path.setAttribute('d', `M${x0},0 C${x0},${ky * .55} ${kx - 40},${ky * .35} ${kx - 40},${ky - 26} Q${kx - 40},${ky} ${kx - 14},${ky} L${kr},${ky}`);
    const len = path.getTotalLength(); path.style.strokeDasharray = len;
    return { path, len };
  }
  function initDrop() {
    const { gsap } = window;
    ctxDrop = gsap.context(() => {
      let t = layoutDrop(); if (!t) return;
      gsap.fromTo(t.path, { strokeDashoffset: t.len }, { strokeDashoffset: 0, ease: 'none',
        scrollTrigger: { trigger: drop, start: 'top 85%', end: 'top 25%', scrub: .6, invalidateOnRefresh: true,
          onRefreshInit: () => { const n = layoutDrop(); if (n) { t = n; } } } });
    }, drop);
  }

  function teardown() {
    [ctxHero, ctxManifest, ctxThread, ctxChapter, ctxBeanie, ctxTshirt, ctxDrop].forEach(c => c && c.revert());
    chapter && chapter.querySelector('[data-enc-frame]')?.style.removeProperty('--fx');
    chapter && chapter.querySelector('[data-enc-frame]')?.style.removeProperty('--fy');
    ctxHero = ctxManifest = ctxThread = ctxChapter = ctxBeanie = ctxTshirt = ctxDrop = null;
    if (bVideoIO) { bVideoIO.disconnect(); bVideoIO = null; }
  }

  function init() {
    hero = document.querySelector('[data-enc-hero]');
    if (!hero) return;
    manifest = document.querySelector('[data-enc-manifest]');
    chapter = document.querySelector('[data-enc-chapter="hoodie"]');
    beanie = document.querySelector('[data-enc-chapter="beanie"]');
    tshirt = document.querySelector('[data-enc-chapter="tshirt"]');
    drop = document.querySelector('[data-enc-drop]');
    video = hero.querySelector('[data-enc-hero-video]');
    glint = hero.querySelector('[data-enc-glint]');
    thread = ensureThreadSvg();
    placeGlint();
    teardown();
    // Video až po prvním vykreslení (LCP = poster); nezávisí na GSAP.
    if (!paused()) { if ('requestIdleCallback' in window) requestIdleCallback(armVideo, { timeout: 600 }); else setTimeout(armVideo, 300); }
    if (paused() || !hasGsap()) { initStatic(); return; }
    initMotion();
  }

  /* ---------------- události ---------------- */
  document.addEventListener('enormity:presale-accepted', () => { revealUntil = performance.now() + 350; tryStartVideo(); });
  new MutationObserver(() => {
    // Tlačítko pauzy (enormity.js přepíná html.en-motion-paused)
    const p = paused();
    if (p && (ctxHero || ctxThread || ctxChapter || ctxBeanie || ctxTshirt || ctxDrop)) { teardown(); initStatic(); }
    else if (!p && !ctxHero && hero && hero.isConnected && hasGsap()) {
      init();
      if (video && videoStarted && !video.ended) { video.classList.add('is-live'); video.play().catch(() => {}); }
    }
  }).observe(root, { attributes: true, attributeFilter: ['class'] });
  reduceMQ.addEventListener('change', () => init());
  mobileMQ.addEventListener('change', () => { init(); if (window.ScrollTrigger) ScrollTrigger.refresh(); });

  document.addEventListener('shopify:section:unload', e => {
    if (e.target.querySelector('[data-enc-hero],[data-enc-manifest],[data-enc-chapter],[data-enc-drop]')) { teardown(); document.querySelector('#MainContent > .enc-thread')?.remove(); thread = null; }
  });
  document.addEventListener('shopify:section:load', e => {
    if (e.target.querySelector('[data-enc-hero],[data-enc-manifest],[data-enc-chapter],[data-enc-drop]')) { bVideoArmed = false; videoArmed = videoStarted = false; init(); }
  });

  /* Jediný sdílený resize posluchač pro interakce (žádné hromadění při reloadu sekcí v editoru). */
  const onResize = new Map();   // element -> fn
  addEventListener('resize', () => onResize.forEach((fn, el) => { if (el.isConnected) fn(); else onResize.delete(el); }), { passive: true });
  window.__encResizeCount = () => onResize.size;   // pro testy

  /* ---------------- 07 LUPA (interakce, funguje i bez pohybu) ---------------- */
  function initLoupe(root2 = document) {
    root2.querySelectorAll('[data-enc-details]').forEach(sec => {
      if (sec.dataset.encLoupeBound) return; sec.dataset.encLoupeBound = '1';
      const stage = sec.querySelector('[data-enc-loupe]'), img = stage.querySelector('[data-enc-loupe-img]'), lens = stage.querySelector('[data-enc-lens]');
      const status = stage.querySelector('[data-enc-loupe-status]'), mats = [...sec.querySelectorAll('[data-enc-mat]')];
      // Přiblížení = kolikrát je zdroj větší než zobrazený snímek (nikdy víc než nativní rozlišení, max 2.4×).
      // Dokud se zdroj načítá (nebo selže), lupa ukazuje základní snímek s mírným přiblížením 1.25×.
      let hires = mats[0]?.dataset.hires, active = false, x = .5, y = .5, cache = {}, Z = 1.25, lensSrc = '';
      const say = t => { status.textContent = ''; requestAnimationFrame(() => { status.textContent = t; }); };
      const draw = () => {
        const w = stage.clientWidth, h = stage.clientHeight, lw = lens.offsetWidth, px = x * w, py = y * h;
        const c = cache[hires];
        Z = c && c.ok ? Math.max(1.25, Math.min(2.4, c.w / w)) : 1.25;
        const src = c && c.ok ? hires : img.currentSrc || img.src;
        if (src !== lensSrc) { lens.style.backgroundImage = `url("${src}")`; lensSrc = src; }
        lens.style.setProperty('--lxpx', px + 'px'); lens.style.setProperty('--lypx', py + 'px');
        lens.style.backgroundSize = `${w * Z}px ${h * Z}px`;
        lens.style.backgroundPosition = `${-(px * Z - lw / 2)}px ${-(py * Z - lw / 2)}px`;
      };
      const setHires = () => {
        const url = hires;
        if (cache[url]) { draw(); return; }
        cache[url] = { ok: false }; stage.classList.add('is-loading');
        const i = new Image();
        i.onload = () => { cache[url] = { ok: true, w: i.naturalWidth }; if (url === hires) { stage.classList.remove('is-loading'); if (active) draw(); } };
        i.onerror = () => { cache[url] = { ok: false, err: true }; if (url === hires) { stage.classList.remove('is-loading'); say('Detail ve vysokém rozlišení se nepodařilo načíst, lupa ukazuje základní snímek.'); } };
        i.src = url; draw();
      };
      const on = (touch) => { if (active) return; active = true; setHires(); stage.classList.add('is-active'); stage.classList.toggle('is-touch', !!touch); draw(); };
      const off = () => { if (!active) return; active = false; stage.classList.remove('is-active', 'is-touch'); };
      const at = e => { const r = stage.getBoundingClientRect(); x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)); y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)); };
      let down = null, raf = 0;
      stage.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') { at(e); on(false); } });
      stage.addEventListener('pointerleave', e => { if (e.pointerType === 'mouse') off(); });
      stage.addEventListener('pointerdown', e => {
        if (e.pointerType === 'mouse') return;
        down = { x: e.clientX, y: e.clientY, wasActive: active, moved: false };
        if (!active) { at(e); on(true); }
        stage.setPointerCapture?.(e.pointerId);
      });
      stage.addEventListener('pointermove', e => {
        if (e.pointerType !== 'mouse' && !down) return;
        if (down && Math.hypot(e.clientX - down.x, e.clientY - down.y) > 6) down.moved = true;
        at(e); if (!raf) raf = requestAnimationFrame(() => { raf = 0; if (active) draw(); });
      });
      stage.addEventListener('pointerup', () => { if (down && down.wasActive && !down.moved) off(); down = null; });
      stage.addEventListener('pointercancel', () => { down = null; });
      stage.addEventListener('keydown', e => {
        const step = e.shiftKey ? .12 : .04;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (active) off(); else on(false); return; }
        if (e.key === 'Escape') { if (active) off(); return; }
        const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
        if (!d) return; e.preventDefault(); if (!active) on(false);
        x = Math.min(1, Math.max(0, x + d[0])); y = Math.min(1, Math.max(0, y + d[1])); draw();
      });
      stage.addEventListener('blur', () => off());
      onResize.set(stage, () => { if (active) draw(); });
      let token = 0;   // rychlé přepínání: platí jen poslední volba
      mats.forEach(b => b.addEventListener('click', () => {
        if (b.getAttribute('aria-pressed') === 'true') return;
        const my = ++token;
        mats.forEach(m => m.setAttribute('aria-pressed', String(m === b)));
        hires = b.dataset.hires;
        const next = new Image(); next.src = b.dataset.src;
        const swap = () => { if (my !== token) return; img.src = b.dataset.src; img.alt = b.dataset.alt; stage.classList.remove('is-swapping', 'is-loading'); lensSrc = ''; if (active) setHires(); };
        stage.classList.add('is-swapping');
        (next.decode ? next.decode() : Promise.resolve()).then(() => setTimeout(swap, reduceMQ.matches ? 0 : 180), swap);
      }));
    });
  }

  /* ---------------- 08 FILMOVÝ PÁS (interakce) ---------------- */
  function initLook(root2 = document) {
    root2.querySelectorAll('[data-enc-look]').forEach(sec => {
      if (sec.dataset.encLookBound) return; sec.dataset.encLookBound = '1';
      const track = sec.querySelector('[data-enc-look-track]'), prev = sec.querySelector('[data-enc-look-prev]'), next = sec.querySelector('[data-enc-look-next]');
      // aria-disabled (ne disabled) – fokus na tlačítku nezmizí, když pás dojede na konec.
      const sync = () => { const max = track.scrollWidth - track.clientWidth - 2; prev.setAttribute('aria-disabled', String(track.scrollLeft <= 2)); next.setAttribute('aria-disabled', String(track.scrollLeft >= max)); };
      let raf = 0; track.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; sync(); }); }, { passive: true });
      // Skok přesně na další/předchozí snímek (spolehlivé i se scroll-snap: mandatory na mobilu).
      const go = dir => {
        const pad = parseFloat(getComputedStyle(track).paddingLeft) || 0, cur = track.scrollLeft;
        const lefts = [...track.children].map(li => li.offsetLeft - pad);
        const target = dir > 0 ? (lefts.find(l => l > cur + 8) ?? track.scrollWidth) : ([...lefts].reverse().find(l => l < cur - 8) ?? 0);
        track.scrollTo({ left: target, behavior: reduceMQ.matches ? 'auto' : 'smooth' });
      };
      prev.addEventListener('click', () => { if (prev.getAttribute('aria-disabled') !== 'true') go(-1); }); next.addEventListener('click', () => { if (next.getAttribute('aria-disabled') !== 'true') go(1); });
      onResize.set(track, sync); sync();
    });
  }
  document.addEventListener('shopify:section:load', e => { initLoupe(e.target); initLook(e.target); });

  const start = () => { init(); if (hero) setupIntro(); initLoupe(); initLook(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
